import { onScopeDispose, ref } from 'vue'

// Универсальный гард несохранённых данных. Любая форма/страница регистрирует
// dirty-чекер через useUnsavedGuard(() => isDirty) — дальше всё само:
// - router.beforeEach (router/index.ts) спрашивает подтверждение перед уходом;
// - beforeunload предупреждает при закрытии/обновлении вкладки;
// - ConfirmLeaveDialog (App.vue) рендерит сам вопрос.
// Чекер снимается автоматически при unmount (onScopeDispose).

const checkers = new Set<() => boolean>()

/** Регистрирует dirty-чекер на время жизни текущего scope (компонента). */
export function useUnsavedGuard(isDirty: () => boolean): void {
  checkers.add(isDirty)
  onScopeDispose(() => checkers.delete(isDirty))
}

/** Есть ли сейчас несохранённые данные хоть в одной зарегистрированной форме. */
export function hasUnsavedChanges(): boolean {
  for (const isDirty of checkers) {
    if (isDirty()) return true
  }
  return false
}

// ── Подтверждение ухода ────────────────────────────────────────────────
// Один вопрос на приложение: confirmLeave() открывает ConfirmLeaveDialog
// и ждёт решения юзера; resolveLeaveConfirm(answer) вызывает диалог.

/** Открыт ли диалог подтверждения (биндится ConfirmLeaveDialog). */
export const leaveConfirmOpen = ref(false)

let pendingResolve: ((leave: boolean) => void) | null = null

/** true — юзер согласился уйти и потерять данные; false — остаёмся. */
export function confirmLeave(): Promise<boolean> {
  // Повторный вопрос поверх открытого — отвечаем «остаться» на предыдущий,
  // чтобы не копить висящие промисы.
  pendingResolve?.(false)
  leaveConfirmOpen.value = true
  return new Promise((resolve) => {
    pendingResolve = resolve
  })
}

export function resolveLeaveConfirm(leave: boolean): void {
  leaveConfirmOpen.value = false
  pendingResolve?.(leave)
  pendingResolve = null
}

// Закрытие/обновление вкладки с несохранёнными данными — нативный
// браузерный confirm (кастомный UI здесь невозможен по спецификации).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (!hasUnsavedChanges()) return
    event.preventDefault()
  })
}
